import React, { useState } from "react";
import { FF, T } from "../lib/constants";
import { BTYPES, lessonBlocks, lessonOrder, blockType, newKey, normalizeOrder, fmtDur, parseDur } from "../lib/blocks";

// Lesopbouw-pop-up (build 2.6), alleen voor de editor. Per les (A of B) kies je de
// blokken en hun volgorde. Laptop: slepen met ⋮⋮; telefoon: onderblad met ⋮-menu.
// Er wordt pas iets opgeslagen bij "Opslaan".
export default function LessonStructure({ mob, les, lv, sub, onSave, onClose }) {
  var [order, setOrder] = useState(function () { return lessonOrder(les); });
  var [times, setTimes] = useState(function () {
    var t = {};
    lessonBlocks(les).forEach(function (b) { t[b.key] = Number(b.data.time) || 0; });
    return t;
  });
  var [menu, setMenu] = useState(null);       // telefoon: sleutel waarvan het ⋮-menu open is
  var [confirm, setConfirm] = useState(null); // sleutel die (met inhoud) verwijderd wordt
  var [drag, setDrag] = useState(null);
  var [over, setOver] = useState(null);
  var [drafts, setDrafts] = useState({}); // tekst in een minutenvak terwijl je typt

  // Voorlopige les om nummering en labels te bepalen.
  var draft = Object.assign({}, les, { order: order });
  var blocks = lessonBlocks(draft, lv);
  var total = order.reduce(function (s, k) { return s + (Number(times[k]) || 0); }, 0);

  function hasContent(k) {
    var d = les && les[k];
    return !!(d && ((d.description && d.description.trim()) || (d.accents && d.accents.length) || d.diagram));
  }
  function setTime(k, v) {
    var n = Math.max(0, Math.min(180, Math.round((Number(v) || 0) * 2) / 2));
    setTimes(function (t) { var c = Object.assign({}, t); c[k] = n; return c; });
  }
  function insertAt(i, type) {
    var k = newKey(type, les, order);
    var o = order.slice(); o.splice(i, 0, k);
    setOrder(normalizeOrder(o));
    setTimes(function (t) { var c = Object.assign({}, t); c[k] = BTYPES[type].time; return c; });
  }
  function remove(k) {
    setOrder(order.filter(function (x) { return x !== k; }));
    setConfirm(null); setMenu(null);
  }
  function askRemove(k) { if (hasContent(k)) setConfirm(k); else remove(k); }
  function move(k, dir) {
    var i = order.indexOf(k), j = i + dir;
    if (j <= 0 || j >= order.length - 1) return; // niet voorbij warming-up / wedstrijdvorm
    var o = order.slice(); o[i] = o[j]; o[j] = k;
    setOrder(normalizeOrder(o)); setMenu(null);
  }
  function dropOn(target) {
    if (!drag || drag === target) return;
    var o = order.filter(function (x) { return x !== drag; });
    var ti = o.indexOf(target);
    // Naar beneden slepen: achter het doel; naar boven: ervoor.
    var after = order.indexOf(drag) < order.indexOf(target);
    o.splice(ti + (after ? 1 : 0), 0, drag);
    setOrder(normalizeOrder(o));
  }
  function save() {
    var n = JSON.parse(JSON.stringify(les || {}));
    Object.keys(n).forEach(function (k) {
      if (k !== "order" && order.indexOf(k) < 0 && n[k] && typeof n[k] === "object" && !Array.isArray(n[k])) delete n[k];
    });
    order.forEach(function (k) {
      if (!n[k]) n[k] = { name: "", description: "", accents: [], time: 0 };
      n[k].time = Number(times[k]) || 0;
    });
    n.order = order;
    onSave(n);
  }

  // Waar komen de knoppen? "+ Serviceblok" na de warming-up en na elke oefening (ook de
  // extra oefening), tenzij er al een serviceblok volgt. "+ Oefening" na de laatste
  // genummerde oefening (en een serviceblok direct daarachter).
  var lastEx = -1;
  blocks.forEach(function (b, i) { if (b.type === "exercise") lastEx = i; });
  var addAfter = lastEx >= 0 ? lastEx : 0;
  while (blocks[addAfter + 1] && blocks[addAfter + 1].type === "service") addAfter++;
  var hasExtra = order.some(function (k) { return blockType(k) === "extra"; });
  // Ook na de extra oefening (en een serviceblok daarachter) kun je een oefening toevoegen.
  var addAfterExtra = -1;
  blocks.forEach(function (b, i) { if (b.type === "extra") addAfterExtra = i; });
  if (addAfterExtra >= 0) { while (blocks[addAfterExtra + 1] && blocks[addAfterExtra + 1].type === "service") addAfterExtra++; }
  // Staat er al een oefening na de extra oefening, dan staat de gewone knop daar al.
  if (addAfterExtra === addAfter || lastEx > addAfterExtra) addAfterExtra = -1;

  var rows = [];
  blocks.forEach(function (b, i) {
    rows.push({ kind: "block", b: b, i: i });
    var next = blocks[i + 1];
    if (b.type !== "match" && b.type !== "service" && next && next.type !== "service") rows.push({ kind: "service", at: i + 1, id: "s" + b.key });
    if (i === addAfter) rows.push({ kind: "add", at: i + 1, extraBtn: true });
    if (i === addAfterExtra) rows.push({ kind: "add", at: i + 1, extraBtn: false });
  });

  var btnBase = { fontFamily: FF, cursor: "pointer" };
  var subText = function (b) {
    if (b.type === "warmup") return "Vast als eerste";
    if (b.type === "match") return "Vast als laatste";
    var d = les && les[b.key];
    if (d && d.description && d.description.trim()) return d.description.trim().split("\n")[0];
    if (b.type === "extra") return "Optioneel, bv. voor een 60-minutenles";
    if (b.type === "service") return "Serveren";
    return "Nog leeg";
  };

  function renderBlock(b) {
    var fixed = b.type === "warmup" || b.type === "match";
    var isDrag = drag === b.key, isOver = over === b.key && drag && drag !== b.key && !fixed;
    if (confirm === b.key) return (
      <div key={b.key} role="alert" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minHeight: 48, padding: "8px 10px", borderRadius: 12, background: "#FDF1F1", border: "1px solid #F0C9C9" }}>
        <span style={{ flex: 1, minWidth: 160, fontSize: 14, color: T.ink }}><b>{b.label}</b> verwijderen? De uitleg en accenten gaan verloren.</span>
        <button type="button" onClick={function () { setConfirm(null); }} style={Object.assign({}, btnBase, { minHeight: 36, padding: "0 12px", borderRadius: 8, border: "1px solid " + T.line, background: "#fff", color: T.primary, fontWeight: 700, fontSize: 13 })}>Nee</button>
        <button type="button" onClick={function () { remove(b.key); }} style={Object.assign({}, btnBase, { minHeight: 36, padding: "0 12px", borderRadius: 8, border: 0, background: "#B83232", color: "#fff", fontWeight: 700, fontSize: 13 })}>Verwijderen</button>
      </div>
    );
    var mi = order.indexOf(b.key);
    return (
      <div key={b.key}
        draggable={!mob && !fixed}
        onDragStart={!mob && !fixed ? function (e) { setDrag(b.key); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", b.key); } catch (er) {} } : undefined}
        onDragEnd={function () { setDrag(null); setOver(null); }}
        onDragOver={!mob && drag ? function (e) { if (!fixed) { e.preventDefault(); setOver(b.key); } } : undefined}
        onDrop={!mob && drag ? function (e) { e.preventDefault(); if (!fixed) dropOn(b.key); setDrag(null); setOver(null); } : undefined}
        data-block={b.key}
        style={{ position: "relative", display: "flex", alignItems: "center", gap: mob ? 8 : 10, minHeight: 48, padding: mob ? "4px 4px 4px 8px" : "4px 6px 4px 10px", borderRadius: 12, background: b.tint || "#fff", border: "1px solid " + (isOver ? T.primary : T.line2), opacity: isDrag ? 0.45 : 1, boxShadow: isOver ? "0 0 0 2px rgba(26,39,68,.15)" : "none" }}>
        {!mob && <span aria-hidden="true" style={{ color: "#A39E94", fontSize: 14, width: 12, cursor: fixed ? "default" : "grab", userSelect: "none" }}>{fixed ? "" : "⋮⋮"}</span>}
        <span style={{ width: mob ? 28 : 30, height: mob ? 28 : 30, borderRadius: 15, background: b.bg, color: b.fg, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flex: "none" }}>{b.icon}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.ink }}>{b.label}</span>
          {!mob && <span style={{ display: "block", fontSize: 12, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subText(b)}</span>}
        </span>
        <span style={{ display: "flex", alignItems: "center", border: "1px solid " + T.line, borderRadius: 8, background: "#fff", height: mob ? 36 : 34, flex: "none" }}>
          <button type="button" aria-label={"Minder minuten " + b.label} onClick={function () { setTime(b.key, (Number(times[b.key]) || 0) - 5); }} style={Object.assign({}, btnBase, { width: mob ? 30 : 26, height: "100%", border: 0, background: "none", color: T.muted, fontSize: 17 })}>−</button>
          <input type="text" inputMode="decimal" aria-label={"Minuten " + b.label} title="Bv. 10, 7:30 of 7,5"
            value={drafts[b.key] != null ? drafts[b.key] : fmtDur(times[b.key])}
            onFocus={function (e) { var el = e.target; setDrafts(function (d) { var c = Object.assign({}, d); c[b.key] = fmtDur(times[b.key]); return c; }); setTimeout(function () { try { el.select(); } catch (er) {} }, 0); }}
            onChange={function (e) { var raw = e.target.value; setDrafts(function (d) { var c = Object.assign({}, d); c[b.key] = raw; return c; }); var v = parseDur(raw); if (v != null) setTime(b.key, v); }}
            onBlur={function () { setDrafts(function (d) { var c = Object.assign({}, d); delete c[b.key]; return c; }); }}
            style={{ width: 52, border: 0, textAlign: "center", fontSize: 14, fontWeight: 700, fontFamily: FF, color: T.ink, background: "transparent", outline: "none", MozAppearance: "textfield", padding: 0 }} />
          <button type="button" aria-label={"Meer minuten " + b.label} onClick={function () { setTime(b.key, (Number(times[b.key]) || 0) + 5); }} style={Object.assign({}, btnBase, { width: mob ? 30 : 26, height: "100%", border: 0, background: "none", color: T.muted, fontSize: 17 })}>+</button>
        </span>
        {!mob && (fixed
          ? <span style={{ width: 32, flex: "none" }} />
          : <button type="button" aria-label={b.label + " verwijderen"} onClick={function () { askRemove(b.key); }} style={Object.assign({}, btnBase, { width: 32, height: 32, border: 0, background: "none", color: "#B83232", fontSize: 18, flex: "none" })}>×</button>)}
        {mob && (fixed
          ? <span style={{ width: 34, flex: "none" }} />
          : <button type="button" aria-label={"Meer acties " + b.label} aria-expanded={menu === b.key} onClick={function () { setMenu(menu === b.key ? null : b.key); }} style={Object.assign({}, btnBase, { width: 34, height: 36, border: 0, background: "none", color: T.muted, fontSize: 20, flex: "none" })}>⋮</button>)}
        {mob && menu === b.key && (
          <div role="menu" style={{ position: "absolute", right: 4, top: "100%", marginTop: 4, zIndex: 5, background: "#fff", border: "1px solid " + T.line, borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,.15)", minWidth: 170, overflow: "hidden" }}>
            {[["Omhoog", mi > 1, function () { move(b.key, -1); }], ["Omlaag", mi < order.length - 2, function () { move(b.key, 1); }], ["Verwijderen", true, function () { setMenu(null); askRemove(b.key); }]].map(function (m) {
              return <button key={m[0]} type="button" role="menuitem" disabled={!m[1]} onClick={m[2]} style={Object.assign({}, btnBase, { display: "block", width: "100%", textAlign: "left", minHeight: 44, padding: "0 14px", border: 0, borderBottom: "1px solid " + T.line, background: "#fff", fontSize: 15, fontWeight: 600, color: !m[1] ? "#C9C4BA" : m[0] === "Verwijderen" ? "#B83232" : T.ink })}>{m[0]}</button>;
            })}
          </div>
        )}
      </div>
    );
  }

  var list = (
    <div style={{ padding: mob ? "10px 16px" : "12px 22px", display: "flex", flexDirection: "column", gap: 4, overflowY: "auto", minHeight: 0 }}>
      {rows.map(function (r) {
        if (r.kind === "block") return renderBlock(r.b);
        if (r.kind === "service") return (
          <button key={r.id} type="button" onClick={function () { insertAt(r.at, "service"); }}
            style={Object.assign({}, btnBase, { alignSelf: "center", minHeight: mob ? 32 : 30, padding: "0 12px", border: "1px dashed #9CCBEA", borderRadius: 999, background: "#EAF5FC", color: "#1F6FA5", fontSize: 12, fontWeight: 700 })}>+ Serviceblok</button>
        );
        return (
          <div key={"add" + r.at} style={{ display: "flex", gap: 6, margin: "2px 0" }}>
            <button type="button" onClick={function () { insertAt(r.at, "exercise"); }}
              style={Object.assign({}, btnBase, { flex: 1, minHeight: mob ? 44 : 40, border: "2px dashed #CFC9BD", borderRadius: 12, background: "transparent", color: T.muted, fontSize: mob ? 13 : 14, fontWeight: 700 })}>{mob ? "+ Oefening" : "+ Oefening toevoegen"}</button>
            {r.extraBtn && !hasExtra && <button type="button" onClick={function () { insertAt(order.length - 1, "extra"); }}
              style={Object.assign({}, btnBase, { flex: 1, minHeight: mob ? 44 : 40, border: "2px dashed " + ((lv && lv.border) || "#CFC2EA"), borderRadius: 12, background: "transparent", color: (lv && (lv.ink || lv.color)) || "#6A4BB0", fontSize: mob ? 13 : 14, fontWeight: 700 })}>+ Extra oefening</button>}
          </div>
        );
      })}
    </div>
  );

  var head = (
    <div style={{ padding: mob ? "10px 16px 12px" : "18px 22px 14px", borderBottom: "1px solid " + T.line, display: "flex", flexDirection: "column", gap: 10, flex: "none" }}>
      {mob && <span aria-hidden="true" style={{ alignSelf: "center", width: 40, height: 4, borderRadius: 2, background: "#CFC9BD" }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: mob ? 18 : 20, fontWeight: 800, color: T.ink }}>Lesopbouw</h2>
          <p style={{ margin: "2px 0 0", fontSize: mob ? 12 : 13, color: T.muted }}>{sub}</p>
        </div>
        <span style={{ fontSize: mob ? 14 : 15, fontWeight: 800, color: T.ink, whiteSpace: "nowrap" }}>{"Totaal " + fmtDur(total, "min")}</span>
      </div>
    </div>
  );

  var foot = (
    <div style={{ padding: mob ? "12px 16px 22px" : "14px 22px", borderTop: "1px solid " + T.line, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flex: "none" }}>
      {!mob && <span style={{ fontSize: 12, color: T.muted }}>Versleep met ⋮⋮ om de volgorde te veranderen</span>}
      <div style={{ display: "flex", gap: 8, flex: mob ? 1 : "none" }}>
        <button type="button" onClick={onClose} style={Object.assign({}, btnBase, { flex: mob ? 1 : "none", minHeight: mob ? 48 : 44, padding: "0 16px", borderRadius: mob ? 12 : 10, border: "1px solid " + T.line, background: "#fff", color: T.primary, fontSize: mob ? 15 : 14, fontWeight: 700 })}>Annuleren</button>
        <button type="button" onClick={save} style={Object.assign({}, btnBase, { flex: mob ? 1 : "none", minHeight: mob ? 48 : 44, padding: "0 18px", borderRadius: mob ? 12 : 10, border: 0, background: T.primary, color: "#fff", fontSize: mob ? 15 : 14, fontWeight: 700 })}>Opslaan</button>
      </div>
    </div>
  );

  return (
    <div onClick={function (e) { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(20,24,36,.5)", display: "flex", alignItems: mob ? "flex-end" : "center", justifyContent: "center", padding: mob ? 0 : 20, fontFamily: FF }}>
      <section role="dialog" aria-modal="true" aria-label="Lesopbouw"
        onClick={function () { if (menu) setMenu(null); }}
        style={{ background: "#fff", width: "100%", maxWidth: mob ? "none" : 580, maxHeight: mob ? "92%" : "90vh", borderRadius: mob ? "20px 20px 0 0" : 18, boxShadow: "0 20px 60px rgba(0,0,0,.25)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {head}
        <div onClick={function (e) { e.stopPropagation(); }} style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: "1 1 auto" }}>{list}</div>
        {foot}
      </section>
    </div>
  );
}
